import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { getActionsColumn, ActionsColumnConfig } from './definitions/getActionsColumn'
import { getAgreementColumn, AgreementColumnConfig } from './definitions/getAgreementColumn'
import { getAssigneesColumn, AssigneesColumnConfig } from './definitions/getAssigneesColumn'
import { getBooleanColumn, BooleanColumnConfig } from './definitions/getBooleanColumn'
import { getCheckboxColumn, CheckboxColumnConfig } from './definitions/getCheckboxColumn'
import { getChipColumn, ChipSingleColumnConfig } from './definitions/getChipColumn'
import { CodeColumnConfig, getCodeColumn } from './definitions/getCodeColumn'
import { getCustomEditableRenderColumn, CustomEditableColumnConfig } from './definitions/getCustomEditableColumn'
import { getCustomRenderColumn, CustomRenderColumnConfig } from './definitions/getCustomRenderColumn'
import { getDateColumn, DateColumnConfig } from './definitions/getDateColumn'
import { getLinkColumn, HyperlinkColumnConfig } from './definitions/getLinkColumns'
import { getMultiChipColumn, ChipMultipleColumnConfig } from './definitions/getMultiChipColumn'
import { getNumberColumn, NumberColumnConfig } from './definitions/getNumberColumn'
import { getPriceColumn, PriceColumnConfig } from './definitions/getPriceColumn'
import { getTextColumn, TextColumnConfig } from './definitions/getTextColumn'

export type ColumnConfig<T extends GridValidRowModel> =
  | TextColumnConfig<T>
  | CodeColumnConfig<T>
  | BooleanColumnConfig<T>
  | ActionsColumnConfig<T>
  | ChipSingleColumnConfig<T>
  | ChipMultipleColumnConfig<T>
  | CustomEditableColumnConfig<T>
  | CustomRenderColumnConfig<T>
  | DateColumnConfig<T>
  | HyperlinkColumnConfig<T>
  | NumberColumnConfig<T>
  | AssigneesColumnConfig<T>
  | AgreementColumnConfig<T>
  | CheckboxColumnConfig<T>
  | PriceColumnConfig<T>

// `columnType?: never` lets the compiler tell the two apart while contextually typing an array literal;
// without it the callbacks of a config (e.g. renderCell params) lose their types.
export type ColumnInput<T extends GridValidRowModel> = ColumnConfig<T> | (GridColDef<T> & { columnType?: never })

type Input<T extends GridValidRowModel> = {
  columnConfig: ColumnInput<T>[]
}

const isColumnConfig = <T extends GridValidRowModel>(column: ColumnInput<T>): column is ColumnConfig<T> => 'columnType' in column

export const useColumnConfig = <T extends GridValidRowModel>({ columnConfig }: Input<T>): GridColDef<T>[] => {
  const { t: tCommon } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()

  return columnConfig
    .map((config) => {
      if (!isColumnConfig(config)) return config
      switch (config.columnType) {
        case 'text':
          return getTextColumn(config)
        case 'code':
          return getCodeColumn(config)
        case 'boolean':
          return getBooleanColumn(config)
        case 'actions':
          return getActionsColumn(config)
        case 'chip-single':
          return getChipColumn(config)
        case 'chip-multiple':
          return getMultiChipColumn(config)
        case 'custom-editable':
          return getCustomEditableRenderColumn(config)
        case 'custom-render':
          return getCustomRenderColumn(config)
        case 'date':
          return getDateColumn(config)
        case 'hyperlink':
          return getLinkColumn(config)
        case 'number':
          return getNumberColumn(config)
        case 'checkbox':
          return getCheckboxColumn(config)
        case 'price':
          return getPriceColumn(config)
        case 'agreement':
          return getAgreementColumn(config)
        case 'assignees':
          return getAssigneesColumn({ ...config, dependencies: { tCommon, enqueueSnackbar } })
        default:
          assertNever(config)
          return null
      }
    })
    .filter((column): column is GridColDef<T> => column !== null)
}
